import { connect } from 'react-redux';
import { get, keyBy, slice } from 'lodash';
import { Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { showErrorMessage } from '@actions/messageconfirmation';

import { Button, Dropdown } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS, DATE_PICKER_DOT_COLOR } from '@commons/DatePickers/constants';
import { exitModal } from '@commons/utils/exportModalWithProgress';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getUserTimezone } from '@commons/utils/date';
import { NONE_DROPDOWN_VALUES } from '@commons/constants/dropdown';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import ExportModalContent from '@lib/inpulse/ExportModal';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

import { supplier as supplierService } from '@services/supplier';
import orderService from '@services/order';

import { getPrevHorizon } from '@selectors/featureProps';
import {
  InputContainer,
  InputRow,
  Footer,
  Header,
  SummaryContainer,
  DatePickerContainer,
  InputRowGroup,
} from './styledComponents';
import { ORDER_AVAILABILITY_STATUS } from './constants';

const BATCH_STORE_IDS = 2;

const modalSx = {
  position: 'relative',
  width: 640,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  overflow: 'visible',
};

export const OrdersFromRecosModal = (props) => {
  const {
    handleClose,
    reloadOrders,
    groups,
    // From Redux
    salesPointStores,
    client: { storeName },
    showErrorMessage,
    prevHorizon,
  } = props;

  /** --- STATES --- */
  const [isConfiguring, setIsConfiguring] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);

  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [titleModal, setTitleModal] = useState('');
  const [storeIdsToProcess, setStoreIdsToProcess] = useState([]);
  const [allStoresWithNoOrder, setAllStoresWithNoOrder] = useState([]);
  const [totalStoreIds, setTotalStoreIds] = useState(0);

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedDateInput, setFocusedDateInput] = useState(null);

  const [availabilityDates, setAvailabilityDates] = useState({
    authorized: [],
    warned: [],
    forbidden: [],
  });

  const [result, setResult] = useState({});

  const translatedStoreName = getClientStoreNameTranslation(storeName, true);

  const startOfMonth = moment().startOf('month');
  const maxFutureDate = moment().add(prevHorizon.future, 'day');
  const TOMORROW = moment.tz(getUserTimezone()).add(1, 'day');
  const salesPointStoresKeyById = keyBy(salesPointStores, 'id');

  const noneGroup = NONE_DROPDOWN_VALUES().MASCULINE;
  const groupsWithNone = [noneGroup, ...groups];
  const isNoneSelected = selectedGroups.some((g) => g.id === noneGroup.id);

  /** --- USE EFFECTS --- */
  useEffect(() => {
    setSelectedSupplier(null);
    setAvailabilityDates({ authorized: [], warned: [], forbidden: [] });

    const hasNone = selectedGroups.some((g) => g.id === noneGroup.id);
    if (!hasNone && selectedGroups.length > 0) {
      const automaticallySelectedStores = salesPointStores.filter((store) =>
        selectedGroups.some((group) => group.storeIds.includes(store.id)),
      );
      setSelectedStores(automaticallySelectedStores);
    } else {
      setSelectedStores([]);
    }
  }, [selectedGroups]);

  useEffect(() => {
    if (!selectedStores.length) {
      return;
    }

    setSelectedSupplier(null);
    setAvailabilityDates({ authorized: [], warned: [], forbidden: [] });
    setIsLoadingSuppliers(true);
    const storeIds = selectedStores.map(({ id }) => id);

    (async () => {
      try {
        /**
         * If the feature is one day enabled for other clients:
         * rework this to have a dedicated endpoint finding the common suppliers of the selected stores
         *
         * For now, the feature being only for FeelnFood, we can use the existing endpoint
         */
        const fetchedSuppliers = await supplierService.getSuppliersOfStores(storeIds, {
          withLinkedData: false,
        });

        setSuppliers(fetchedSuppliers);

        if (!fetchedSuppliers.length) {
          showErrorMessage(
            i18next.t('ORDERS.ORDERS.BATCH_CREATION_FROM_RECOMMENDATION_NO_SUPPLIER_PROFILE'),
          );
        }
      } catch {
        showErrorMessage(i18next.t('ADMIN.SUPPLIERS.FETCH_FAILURE'));
      } finally {
        setIsLoadingSuppliers(false);
      }
    })();
  }, [selectedStores]);

  useEffect(() => {
    if (!selectedSupplier) {
      return;
    }

    (async () => {
      try {
        const orderAvailabilities = await orderService.getOrderAvailabilities(
          selectedStores.map(({ id }) => id),
          selectedSupplier.id,
          startOfMonth.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          maxFutureDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        );

        const authorized = [];
        const warned = [];
        const forbidden = [];

        orderAvailabilities.forEach(({ date, status }) => {
          const momentDate = moment.tz(date, getUserTimezone());
          if (status === ORDER_AVAILABILITY_STATUS.CAN_ORDER && momentDate.isAfter(moment())) {
            authorized.push(momentDate);
          } else if (status === ORDER_AVAILABILITY_STATUS.CAN_ORDER_MORE) {
            warned.push(momentDate);
          } else if (status === ORDER_AVAILABILITY_STATUS.CANNOT_ORDER) {
            forbidden.push(momentDate);
          }
        });

        setAvailabilityDates({ authorized, warned, forbidden });
      } catch {
        showErrorMessage(i18next.t('ADMIN.SUPPLIERS.FETCH_FAILURE'));
      }
    })();
  }, [selectedSupplier]);

  /** --- FUNCTIONS --- */
  const handleDatesChange = ({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const validateOrdersFromRecos = async () => {
    const storeIds = selectedStores.map(({ id }) => id);

    try {
      const result = await orderService.validateOrdersFromRecos(
        storeIds,
        selectedSupplier.id,
        startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
        getUserTimezone(),
      );

      setResult(result);
      setIsConfiguring(false);
    } catch {
      showErrorMessage(i18next.t('ORDERS.ORDERS.VALIDATE_FAILURE'));
    }
  };

  const returnToConfiguration = () => {
    setIsConfiguring(true);
  };

  const createOrdersFromRecommendations = () => {
    const storeIds = get(result, 'storesWithoutOrders', []);

    setTitleModal(
      i18next.t('ORDERS.ORDERS.MODAL_CREATE_FROM_RECOMMENDATION_ONGOING', {
        count: selectedStores.length,
      }),
    );
    setProgress(0);
    setIsLoading(true);
    setAllStoresWithNoOrder([]);
    setTotalStoreIds(storeIds.length);
    setStoreIdsToProcess([...storeIds]);
    setIsCreating(true);
  };

  useEffect(() => {
    if (!isCreating) {
      return;
    }

    const processBatch = async () => {
      if (!storeIdsToProcess.length && !totalStoreIds) {
        return;
      }

      if (!storeIdsToProcess.length) {
        setProgress(100);

        if (!allStoresWithNoOrder.length) {
          setTitleModal(i18next.t('ORDERS.ORDERS.BATCH_CREATION_FROM_RECOMMENDATION_SUCCESS'));
        } else {
          setTitleModal(
            i18next.t('ORDERS.ORDERS.BATCH_CREATION_FROM_RECOMMENDATION_NO_ORDER', {
              count: allStoresWithNoOrder.length,
              storeName: allStoresWithNoOrder.join(', '),
            }),
          );
        }

        setIsLoading(false);
        return;
      }

      const batchStoreIds = slice(storeIdsToProcess, 0, BATCH_STORE_IDS);
      const remainingStoreIds = slice(storeIdsToProcess, BATCH_STORE_IDS);

      try {
        const { storesWithNoOrder } = await orderService.createOrdersFromRecos(
          batchStoreIds,
          selectedSupplier.id,
          startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
          getUserTimezone(),
        );

        setAllStoresWithNoOrder((prev) => [...prev, ...storesWithNoOrder]);

        const processedCount = totalStoreIds - remainingStoreIds.length;
        setProgress((processedCount / totalStoreIds) * 100);

        setStoreIdsToProcess(remainingStoreIds);
      } catch {
        setTitleModal(i18next.t('ORDERS.ORDERS.BATCH_CREATION_FROM_RECOMMENDATION_FAILURE'));
        setIsLoading(false);
      }
    };

    processBatch();
  }, [storeIdsToProcess, isCreating]);

  const calendarInfo = selectedSupplier
    ? {
        dotsInformations: [
          {
            dotColor: DATE_PICKER_DOT_COLOR.GREEN,
            dotInfo: i18next.t('ORDERS.ORDERS.FORM_INFO_MODAL_DELIVERY_AVAILABLE'),
          },
          {
            dotColor: DATE_PICKER_DOT_COLOR.ORANGE,
            dotInfo: i18next.t(
              'ORDERS.ORDERS.FORM_INFO_MODAL_DELIVERY_AVAILABLE_BUT_WITH_ALREADY_EXISTING',
            ),
          },
          {
            dotColor: DATE_PICKER_DOT_COLOR.RED,
            dotInfo: i18next.t('ORDERS.ORDERS.FORM_INFO_MODAL_DELIVERY_UNAVAILABLE'),
          },
        ],
      }
    : null;

  const availableStores =
    selectedGroups.length > 0 && !isNoneSelected
      ? salesPointStores.filter((store) =>
          selectedGroups.some((group) => group.storeIds.includes(store.id)),
        )
      : salesPointStores;

  if (isCreating) {
    return (
      <Box sx={modalSx}>
        <ExportModalContent
          closeModal={() => {
            handleClose();
            reloadOrders();
          }}
          exitModal={exitModal}
          isLoading={isLoading}
          progress={progress}
          setLoading={setIsLoading}
          titleModal={titleModal}
        />
      </Box>
    );
  }

  if (isConfiguring) {
    return (
      <Box sx={modalSx}>
        <Header>
          <img height={16} src={'/images/inpulse/add-black-small.svg'} width={16} />
          <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H1}>
            {i18next.t('ORDERS.ORDERS.MODAL_CREATE_FROM_RECOMMENDATION_TITLE')}
          </Text>
        </Header>
        {!!groups.length && (
          <InputRowGroup>
            <InputRow>
              <Dropdown
                iconSrc="/images/inpulse/group-black-small.svg"
                isUniqueSelection={false}
                items={groupsWithNone}
                label={i18next.t('GENERAL.GROUPS')}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItems={selectedGroups}
                isRequired
                onSelectionChange={setSelectedGroups}
              />
              <Dropdown
                iconSrc="/images/inpulse/store-black-small.svg"
                isDisabled={!selectedGroups.length}
                isUniqueSelection={false}
                items={availableStores}
                label={translatedStoreName}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItems={selectedStores}
                isRequired
                onSelectionChange={setSelectedStores}
              />
            </InputRow>
            <InputRow>
              <Dropdown
                iconSrc="/images/inpulse/pin-black-small.svg"
                isDisabled={!selectedStores.length || !suppliers.length || isLoadingSuppliers}
                items={suppliers}
                label={i18next.t('GENERAL.SUPPLIER')}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItem={selectedSupplier}
                isRequired
                onSelectionChange={setSelectedSupplier}
              />
              <DatePickerContainer>
                <Text>{i18next.t('GENERAL.DATES')}</Text>
                <PeriodDatePicker
                  authorizedDates={availabilityDates.authorized}
                  calendarInfo={calendarInfo}
                  customStyle={{ width: '288px' }}
                  disabled={!selectedStores.length || !selectedSupplier}
                  endDate={endDate}
                  focusedDateInput={focusedDateInput}
                  forbiddenDates={availabilityDates.forbidden}
                  maxFutureDate={maxFutureDate}
                  maxPastDate={TOMORROW}
                  setFocusedDateInput={setFocusedDateInput}
                  startDate={startDate}
                  timezone={getUserTimezone()}
                  warnedDates={availabilityDates.warned}
                  onDatesChange={handleDatesChange}
                />
              </DatePickerContainer>
            </InputRow>
          </InputRowGroup>
        )}
        {!groups.length && (
          <>
            <InputContainer paddingTop={16}>
              <Dropdown
                iconSrc="/images/inpulse/pin-black-small.svg"
                isUniqueSelection={false}
                items={availableStores}
                label={translatedStoreName}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItems={selectedStores}
                isRequired
                onSelectionChange={setSelectedStores}
              />
            </InputContainer>
            <InputContainer>
              <Dropdown
                iconSrc="/images/inpulse/pin-black-small.svg"
                isDisabled={!selectedStores.length || !suppliers.length || isLoadingSuppliers}
                items={suppliers}
                label={i18next.t('GENERAL.SUPPLIER')}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItem={selectedSupplier}
                isRequired
                onSelectionChange={setSelectedSupplier}
              />
            </InputContainer>
            <InputContainer>
              <DatePickerContainer>
                <Text>{i18next.t('GENERAL.DATES')}</Text>
                <PeriodDatePicker
                  authorizedDates={availabilityDates.authorized}
                  calendarInfo={calendarInfo}
                  disabled={!selectedStores.length || !selectedSupplier}
                  endDate={endDate}
                  focusedDateInput={focusedDateInput}
                  forbiddenDates={availabilityDates.forbidden}
                  maxFutureDate={maxFutureDate}
                  maxPastDate={TOMORROW}
                  setFocusedDateInput={setFocusedDateInput}
                  startDate={startDate}
                  timezone={getUserTimezone()}
                  warnedDates={availabilityDates.warned}
                  onDatesChange={handleDatesChange}
                />
              </DatePickerContainer>
            </InputContainer>
          </>
        )}
        <Footer>
          <Button
            color={'inpulse-outline'}
            handleClick={handleClose}
            icon={'/images/inpulse/close-black-small.svg'}
            label={i18next.t('GENERAL.CANCEL')}
          />
          <Button
            color={'inpulse-default'}
            handleClick={validateOrdersFromRecos}
            icon={'/images/inpulse/add-white-small.svg'}
            isDisabled={
              !selectedStores.length ||
              !selectedSupplier ||
              !startDate ||
              !endDate ||
              (groups.length > 0 && !selectedGroups.length)
            }
            label={i18next.t('GENERAL.CREATE')}
          />
        </Footer>
      </Box>
    );
  }

  const numberOfOrders = get(result, 'storesWithoutOrders.length', 0);
  const storesWithNoValidDeliveryDay = get(result, 'storesWithNoValidDeliveryDay', []);
  const storesWithExistingOrders = get(result, 'storesWithExistingOrders', []);
  const formattedStartDate = startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

  const hasNoEligibleStores = numberOfOrders === 0;
  const hasPartiallyEligibleStores = numberOfOrders > 0 && storesWithNoValidDeliveryDay.length > 0;
  const hasStoresWithExistingOrders = storesWithExistingOrders.length > 0;

  return (
    <Box sx={modalSx}>
      <Header>
        <img height={16} src={'/images/inpulse/add-black-small.svg'} width={16} />
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H1}>
          {i18next.t('ORDERS.ORDERS.MODAL_CREATE_FROM_RECOMMENDATION_TITLE')}
        </Text>
      </Header>

      <SummaryContainer>
        {hasNoEligibleStores ? (
          <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
            <Trans
              components={{ bold: <strong /> }}
              i18nKey="ORDERS.ORDERS.NO_ORDERS_FROM_RECOMMENDATION"
              values={{ supplierName: selectedSupplier.name, startDate: formattedStartDate }}
            />
          </Text>
        ) : (
          <>
            <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
              <Trans
                components={{ bold: <strong /> }}
                count={numberOfOrders}
                i18nKey="ORDERS.ORDERS.NUMBER_OF_ORDERS_FROM_RECOMMENDATION"
                values={{
                  count: numberOfOrders,
                  supplierName: selectedSupplier.name,
                  startDate: formattedStartDate,
                }}
              />
            </Text>
            {hasPartiallyEligibleStores && (
              <>
                <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
                  {i18next.t('ORDERS.ORDERS.STORES_WITH_NO_VALID_DELIVERY_CONDITIONS', {
                    storeName: translatedStoreName,
                  })}
                </Text>
                {storesWithNoValidDeliveryDay.map((storeId) => (
                  <Text
                    color={ENUM_COLORS.DARKER}
                    font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}
                    key={storeId}
                  >
                    {salesPointStoresKeyById[storeId]?.name}
                  </Text>
                ))}
              </>
            )}
          </>
        )}
        {hasStoresWithExistingOrders && (
          <>
            <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL}>
              {i18next.t('ORDERS.ORDERS.STORES_WITH_EXISTING_ORDERS')}
            </Text>
            {storesWithExistingOrders.map((storeId) => (
              <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_MIDDLE_NORMAL} key={storeId}>
                {salesPointStoresKeyById[storeId]?.name}
              </Text>
            ))}
          </>
        )}
      </SummaryContainer>
      <Footer>
        <Button
          color={'inpulse-outline'}
          handleClick={returnToConfiguration}
          icon={'/images/inpulse/arrow-left-ip-black.svg'}
          label={i18next.t('GENERAL.BACK')}
        />
        {hasNoEligibleStores ? (
          <Button
            color={'inpulse-default'}
            handleClick={handleClose}
            icon={'/images/inpulse/close-white-small.svg'}
            label={i18next.t('GENERAL.CLOSE')}
          />
        ) : (
          <Button
            color={'inpulse-default'}
            handleClick={createOrdersFromRecommendations}
            icon={'/images/inpulse/check-white-small.svg'}
            isDisabled={!selectedStores.length}
            label={i18next.t('GENERAL.CONFIRM')}
          />
        )}
      </Footer>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  salesPointStores: getSalesPointStores(state.baseReducer.activeStores),
  modal: state.modalReducer,
  currency: state.baseReducer.currency,
  prevHorizon: getPrevHorizon(state.baseReducer.userRights),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(OrdersFromRecosModal);
